import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { InvitationCard, getTemplate } from "../components/InvitationCard";
import { Loader2, MapPin, ThumbsUp, ThumbsDown, Check, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvitationView = () => {
  const { id } = useParams();
  const [qs] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // "yes" | "no"
  const [name, setName] = useState(() => new URLSearchParams(window.location.search).get("n") || "");
  const [guests, setGuests] = useState(1);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invitations/${id}`)
      .then((r) => setInv(r.data))
      .catch(() => setInv(false))
      .finally(() => setLoading(false));
  }, [id]);

  const submit = async () => {
    if (!name.trim() || !status) return;
    setSending(true);
    try {
      await axios.post(`${API}/invitations/${id}/rsvp`, { name: name.trim(), status, guests: Number(guests) || 1, message: message.trim(), guestId: qs.get("g") || "" });
      setDone(true);
    } catch {}
    finally { setSending(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF5F0]"><Loader2 className="w-8 h-8 animate-spin text-[#9E2A3B]" /></div>;
  }
  if (!inv) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF5F0] text-[#7C6E70]">Davetiye bulunamadı.</div>;
  }

  const t = getTemplate(inv.template);

  return (
    <div className="min-h-screen py-8 px-4 flex flex-col items-center" style={{ background: "#FBF5F0" }} data-testid="invitation-view">
      <div className="w-full max-w-md">
        <div className="rounded-3xl overflow-hidden shadow-2xl border border-[#F0DDE0] min-h-[520px]">
          <InvitationCard template={inv.template} data={inv} />
        </div>

        {inv.mapUrl && (
          <a href={inv.mapUrl} target="_blank" rel="noreferrer" data-testid="inv-map-link"
            className="mt-4 flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-[#F0DDE0] text-[#9E2A3B] text-sm font-medium hover:shadow-md transition-all">
            <MapPin className="w-4 h-4" /> Konumu Haritada Aç
          </a>
        )}

        {/* RSVP */}
        <div className="mt-4 bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="rsvp-form">
          {done ? (
            <div className="text-center py-4" data-testid="rsvp-done">
              <div className="w-14 h-14 rounded-full bg-[#E6F4EA] flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-[#2f7d4f]" />
              </div>
              <p className="font-display text-xl text-[#2A2224]">Yanıtınız alındı, teşekkürler!</p>
              <p className="text-sm text-[#7C6E70] mt-1">{status === "yes" ? "Sizi düğünde görmek için sabırsızlanıyoruz 💍" : "Anlayışınız için teşekkürler."}</p>
            </div>
          ) : (
            <>
              <p className="font-display text-xl text-[#2A2224] text-center flex items-center justify-center gap-2">
                <Heart className="w-5 h-5 text-[#C15B6B]" fill="#C15B6B" /> Katılım Durumunuz
              </p>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <button onClick={() => setStatus("yes")} data-testid="rsvp-choose-yes"
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium border-2 transition-all ${status === "yes" ? "border-[#2f7d4f] bg-[#E6F4EA] text-[#2f7d4f]" : "border-[#F0DDE0] text-[#7C6E70] hover:border-[#2f7d4f]"}`}>
                  <ThumbsUp className="w-4 h-4" /> Katılıyorum
                </button>
                <button onClick={() => setStatus("no")} data-testid="rsvp-choose-no"
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium border-2 transition-all ${status === "no" ? "border-[#C0392B] bg-[#FBEAEA] text-[#C0392B]" : "border-[#F0DDE0] text-[#7C6E70] hover:border-[#C0392B]"}`}>
                  <ThumbsDown className="w-4 h-4" /> Katılamıyorum
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Adınız Soyadınız" data-testid="rsvp-name"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]" />
                {status === "yes" && (
                  <div>
                    <label className="text-sm text-[#7C6E70]">Kaç kişi geleceksiniz?</label>
                    <input type="number" min="1" value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="rsvp-guests"
                      className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]" />
                  </div>
                )}
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Mesajınız (opsiyonel)" data-testid="rsvp-message"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B] resize-y" />
                <button onClick={submit} disabled={sending || !name.trim() || !status} data-testid="rsvp-submit"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-lg transition-all disabled:opacity-60">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Yanıtı Gönder
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-[#c9bcbe] mt-5">DüğünSenin.com ile hazırlanmıştır</p>
      </div>
    </div>
  );
};

export default InvitationView;
